import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import type { ConversationPage, ConversationRow, ConversationSlice, Filters } from "../lib/api.ts";
import { num } from "../lib/format.ts";
import { DrawerActions } from "./exportButton.tsx";
import { exportSheetsToExcel, fetchAllPages, type ExportColumn } from "../lib/tableExport.ts";
import { plural, t } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";
import { Th, useSortable, type SortColumn } from "./sortable.tsx";

/**
 * Список ДИАЛОГОВ открытой линии под цифрой на экране.
 *
 * ПОЧЕМУ ТРЕТЬЯ ПАНЕЛЬ, А НЕ СРЕЗ В ДВУХ СУЩЕСТВУЮЩИХ. У диалога свой объект и
 * свой вопрос — «кто написал, ответили ли ему и куда идти отвечать». Список
 * лидов под карточкой «Никто не ответил» был бы короче собственного числа:
 * владельцем диалога бывает и сделка, и контакт, а замер 18.09.2026 даёт 2 854
 * диалога на лидах против 323 на сделках и одного на контакте.
 *
 * ПОЧЕМУ ПАНЕЛЬ, А НЕ СПИСОК В КАРТОЧКЕ. Раньше карточка печатала все
 * обращения плашками прямо в дайджест — на том же замере это 390 строк одним
 * полотном, поверх которого не видно ни остальных поводов, ни таблицы
 * ближайших праздников. Заказчик: «при нажатии на число огромный список
 * вываливается, надо чтобы как и в других drill down открывалось меню сбоку».
 *
 * ГЛАВНОЕ ЗДЕСЬ — ССЫЛКА НА СУЩНОСТЬ. Обращение без ответа это повод пойти и
 * ответить; список без перехода в карточку заставляет искать клиента руками по
 * названию парка и времени сообщения.
 */

/** Проза этой панели. Один блок в начале файла — см. App.tsx. */
const TX = {
  conversationsCount: {
    one: { ru: "обращение", en: "inquiry" },
    few: { ru: "обращения", en: "inquiries" },
    many: { ru: "обращений", en: "inquiries" },
  },
  noMatch: { ru: "Под этот срез обращений нет.", en: "No inquiries match this slice." },
  colWrote: { ru: "Клиент написал", en: "Client wrote" },
  colWaiting: { ru: "Ждёт", en: "Waiting" },
  colLine: { ru: "Линия", en: "Line" },
  colAnswer: { ru: "Ответ", en: "Reply" },
  colEntity: { ru: "Где в CRM", en: "Where in the CRM" },
  colMessages: { ru: "Сообщений", en: "Messages" },
  noAnswer: { ru: "никто не ответил", en: "nobody replied" },
  byBot: { ru: "бот", en: "bot" },
  byHuman: { ru: "сотрудник", en: "a person" },
  byOutgoing: { ru: "исходящее", en: "outgoing" },
  noEntity: { ru: "диалог ни к чему не прикреплён", en: "not attached to anything" },
  entityLead: { ru: "лид", en: "lead" },
  entityDeal: { ru: "сделка", en: "deal" },
  entityContact: { ru: "контакт", en: "contact" },
  unitHours: { ru: "ч", en: "h" },
  unitMinutes: { ru: "мин", en: "min" },
  firstPage: { ru: "В начало", en: "First" },
  previousPage: { ru: "Назад", en: "Previous" },
  nextPage: { ru: "Далее", en: "Next" },
  pageOf: { ru: "стр.", en: "page" },
  goToPage: { ru: "Перейти к странице", en: "Go to page" },
  /* --- колонки выгрузки в Excel: вторая строка ячейки — отдельной колонкой */
  exReplyMinutes: { ru: "Ответ через, мин", en: "Reply after, min" },
  exTitle: { ru: "Название в CRM", en: "Title in the CRM" },
  exId: { ru: "ID в CRM", en: "CRM ID" },
  exStage: { ru: "Стадия", en: "Stage" },
  exOwner: { ru: "Ответственный", en: "Owner" },
  exLink: { ru: "Ссылка", en: "Link" },
  /* --- листы выгрузки: имя листа Excel — не длиннее 31 знака */
  sheetLeads: { ru: "Лиды", en: "Leads" },
  sheetDeals: { ru: "Сделки", en: "Deals" },
  sheetOther: { ru: "Без лида и сделки", en: "No lead or deal" },
} as const;

/** Кем был первый ответ. Ключи — значения поля, а не наши выдумки. */
const REPLY_BY = {
  bot: TX.byBot,
  human: TX.byHuman,
  outgoing: TX.byOutgoing,
} as const;

const ENTITY_LABEL = {
  lead: TX.entityLead,
  deal: TX.entityDeal,
  contact: TX.entityContact,
} as const;

export type ConversationDrill = {
  title: string;
  /** Пояснение под заголовком: по какому именно условию собран список. */
  hint?: string;
  slice: ConversationSlice;
  /**
   * Своё окно вместо периода с верхней панели. Дайджест период не применяет
   * вовсе: карточка «Никто не ответил» считает свои семь дней, и список обязан
   * отбирать ровно ту совокупность, которую посчитала карточка.
   */
  filters?: Partial<Filters>;
};

/** Сколько строк за раз. Потолок сервера — 500. */
const PAGE = 200;

/** Колонки выгрузки в Excel — те же, что у таблицы; вторая строка ячейки — рядом. */
function conversationExportColumns(): Array<ExportColumn<ConversationRow>> {
  return [
    { title: t(TX.colWrote), kind: "datetime", width: 17, value: (r) => r.wrote_at },
    { title: `${t(TX.colWaiting)}, ${t(TX.unitHours)}`, kind: "number", width: 10, value: (r) => r.hours_waiting },
    { title: t(UI.park), width: 24, value: (r) => r.park_name },
    { title: t(TX.colLine), width: 24, value: (r) => r.line_name },
    {
      title: t(TX.colAnswer),
      width: 18,
      value: (r) => (!r.answered ? t(TX.noAnswer) : r.first_reply_by === null ? null : t(REPLY_BY[r.first_reply_by])),
    },
    { title: t(TX.exReplyMinutes), kind: "number", width: 12, value: (r) => (r.answered ? r.reply_minutes : null) },
    { title: t(TX.colMessages), kind: "number", width: 10, value: (r) => r.client_messages },
    { title: t(TX.colEntity), width: 14, value: (r) => (r.entity === null ? t(TX.noEntity) : t(ENTITY_LABEL[r.entity])) },
    { title: t(TX.exTitle), width: 30, value: (r) => r.entity_title },
    { title: t(TX.exId), kind: "number", width: 10, value: (r) => r.entity_id },
    { title: t(TX.exStage), width: 18, value: (r) => r.entity_stage },
    { title: t(TX.exOwner), width: 22, value: (r) => r.manager_name },
    { title: t(TX.exLink), width: 50, value: (r) => r.link },
  ];
}

const COLS: Record<string, SortColumn<ConversationRow>> = {
  wrote: { kind: "text", get: (r) => r.wrote_at },
  waiting: { kind: "number", get: (r) => r.hours_waiting },
  park: { kind: "text", get: (r) => r.park_name },
  line: { kind: "text", get: (r) => r.line_name },
  /* Сортируется НАПЕЧАТАННОЕ: у неотвеченного в ячейке текст, а не число. */
  answer: { kind: "number", get: (r) => (r.answered ? r.reply_minutes : null) },
  entity: { kind: "text", get: (r) => r.entity_title },
  messages: { kind: "number", get: (r) => r.client_messages },
};

export function ConversationDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: ConversationDrill | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<ConversationPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Что человек НАБРАЛ в поле номера страницы, до подтверждения. */
  const [typed, setTyped] = useState("");

  const key = drill === null ? "" : JSON.stringify([drill.slice, filters]);

  /* Смена среза возвращает на первую страницу: иначе открытый на пятнадцатой
     странице список показал бы пустоту под новым числом. */
  useEffect(() => {
    setOffset(0);
  }, [key]);

  useEffect(() => {
    if (drill === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .conversations(filters, drill.slice, PAGE, offset)
      .then((p) => {
        if (alive) setPage(p);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset]);

  /** Escape закрывает — панель модальная, и мышь до крестика тянуть не нужно. */
  useEffect(() => {
    if (drill === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill, onClose]);

  const rows = page?.rows ?? [];
  const total = page?.total ?? 0;

  /* Сортировка — только когда весь срез уместился на странице: порядок задаёт
     сервер (дольше всех ждущие сверху), и сортировка двухсот строк из четырёхсот
     напечатала бы «самое долгое ожидание» из середины среза. */
  const S = useSortable(rows, COLS, { partial: total > rows.length });

  if (drill === null) return null;

  const shown = offset + rows.length;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  /*
    Весь список под цифрой: целиком на экране — в отсортированном порядке, иначе
    со всех страниц. Лиды и сделки — отдельными листами (просьба 31.10.2026):
    отвечают на них разные люди в разных карточках. Диалоги на контакте или ни
    к чему не прикреплённые — третьим листом, только если такие есть: иначе
    сумма двух листов молча не сошлась бы с числом на плитке.
  */
  const runExport = async (progress: (done: number, total: number) => void): Promise<void> => {
    const all =
      total <= rows.length
        ? S.rows
        : await fetchAllPages((limit, off) => api.conversations(filters, drill.slice, limit, off), progress);
    const rest = all.filter((r) => r.entity !== "lead" && r.entity !== "deal");
    await exportSheetsToExcel(drill.title, conversationExportColumns(), [
      { name: t(TX.sheetLeads), rows: all.filter((r) => r.entity === "lead") },
      { name: t(TX.sheetDeals), rows: all.filter((r) => r.entity === "deal") },
      ...(rest.length > 0 ? [{ name: t(TX.sheetOther), rows: rest }] : []),
    ]);
  };
  const current = Math.floor(offset / PAGE) + 1;

  const jump = (): void => {
    const n = Number(typed);
    setTyped("");
    if (!Number.isFinite(n)) return;
    const clamped = Math.min(Math.max(Math.trunc(n), 1), pages);
    setOffset((clamped - 1) * PAGE);
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">{drill.title}</div>
            <div className="drawer-hint">
              {loading && page === null
                ? t(UI.loading)
                : `${num(total)} ${plural(total, TX.conversationsCount)}`}
              {drill.hint ? ` · ${drill.hint}` : ""}
            </div>
          </div>
          <DrawerActions onClose={onClose} exportRun={runExport} exportDisabled={loading || total === 0} />
        </div>

        <div className="drawer-body">
          {error !== null && <div className="error">{error}</div>}

          {error === null && !loading && rows.length === 0 && (
            <div className="empty">{t(TX.noMatch)}</div>
          )}

          {rows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <Th sort={S} col="wrote">{t(TX.colWrote)}</Th>
                    <Th sort={S} col="waiting" num>{t(TX.colWaiting)}</Th>
                    <Th sort={S} col="park">{t(UI.park)}</Th>
                    <Th sort={S} col="line">{t(TX.colLine)}</Th>
                    <Th sort={S} col="answer">{t(TX.colAnswer)}</Th>
                    <Th sort={S} col="messages" num>{t(TX.colMessages)}</Th>
                    <Th sort={S} col="entity">{t(TX.colEntity)}</Th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {S.rows.map((r) => (
                    <tr key={r.session_id}>
                      <td className="nowrap">{r.wrote_at ?? "—"}</td>
                      {/* Часы ожидания только у неотвеченных: у отвеченного ноль
                          читался бы как мгновенный ответ. */}
                      <td className="num">
                        {r.hours_waiting === null ? (
                          "—"
                        ) : (
                          <span className="tag tag-warn">
                            {num(r.hours_waiting)} {t(TX.unitHours)}
                          </span>
                        )}
                      </td>
                      <td>{r.park_name ?? "—"}</td>
                      <td>{r.line_name ?? "—"}</td>
                      <td>
                        {!r.answered ? (
                          <span className="sub">{t(TX.noAnswer)}</span>
                        ) : (
                          <>
                            {r.first_reply_by === null
                              ? "—"
                              : t(REPLY_BY[r.first_reply_by])}
                            {r.reply_minutes !== null && (
                              <div className="sub">
                                {num(r.reply_minutes)} {t(TX.unitMinutes)}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="num">{num(r.client_messages)}</td>
                      {/*
                        Сущность и переход к ней — то, ради чего список
                        открывают. Печатается и вид записи: «сделка» и «лид»
                        требуют разных действий, а по одному заголовку они
                        неразличимы.
                      */}
                      <td>
                        {r.entity === null || r.link === null ? (
                          <span className="sub">{t(TX.noEntity)}</span>
                        ) : (
                          <>
                            <a href={r.link} target="_blank" rel="noreferrer">
                              {r.entity_title === null || r.entity_title === ""
                                ? `#${r.entity_id}`
                                : r.entity_title}
                            </a>
                            <div className="sub">
                              {t(ENTITY_LABEL[r.entity])}
                              {r.entity_stage !== null ? ` · ${r.entity_stage}` : ""}
                              {r.manager_name !== null ? ` · ${r.manager_name}` : ""}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="nowrap">
                        {r.link !== null && (
                          <a className="btn-ghost" href={r.link} target="_blank" rel="noreferrer">
                            Bitrix ↗
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > rows.length && (
            <div className="drawer-foot">
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(0)}
                >
                  {t(TX.firstPage)}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  {t(TX.previousPage)}
                </button>
              </span>

              <span className="sub" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {num(offset + 1)}–{num(shown)} {t(UI.of)} {num(total)}
                <label style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: 6 }}>
                  {t(TX.pageOf)}
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label={t(TX.goToPage)}
                    title={t(TX.goToPage)}
                    value={typed === "" ? String(current) : typed}
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") jump();
                    }}
                    onBlur={jump}
                    style={{ width: 54, textAlign: "center" }}
                  />
                  {t(UI.of)} {num(pages)}
                </label>
              </span>

              <button
                type="button"
                className="btn-ghost"
                disabled={shown >= total || loading}
                onClick={() => setOffset(offset + PAGE)}
              >
                {t(TX.nextPage)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
